import { useState } from "react";

// Encode form data the way Netlify expects
function encode(data) {
    return Object.keys(data)
        .map(
            (key) =>
                encodeURIComponent(key) + "=" + encodeURIComponent(data[key])
        )
        .join("&");
}

function FormFeedback({ status, text }) {
    if (!status) return null;
    return (
        <div
            data-testid={`form-feedback-${status}`}
            className={`mt-5 text-sm ${
                status === "success" ? "text-[#0D1B2A]" : "text-[#9B2C2C]"
            }`}
        >
            {text}
        </div>
    );
}

function NewsletterForm() {
    const [status, setStatus] = useState(null);
    const [email, setEmail] = useState("");
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const res = await fetch("/", {
                method: "POST",
                headers: { "Content-Type": "application/x-www-form-urlencoded" },
                body: encode({
                    "form-name": "newsletter",
                    "bot-field": "",
                    email,
                }),
            });
            if (!res.ok) throw new Error("submit failed");
            setStatus("success");
            setEmail("");
        } catch (err) {
            setStatus("error");
        } finally {
            setLoading(false);
        }
    };

    return (
        <form
            data-testid="newsletter-form"
            name="newsletter"
            method="POST"
            data-netlify="true"
            netlify-honeypot="bot-field"
            onSubmit={submit}
            className="border border-[#E0E1DD] bg-[#FDFDFC] p-8 md:p-10 flex flex-col h-full"
        >
            <input type="hidden" name="form-name" value="newsletter" />
            <p className="hidden">
                <label>
                    Ne pas remplir : <input name="bot-field" />
                </label>
            </p>

            <span className="eyebrow">Lettre d'information</span>
            <h3 className="h3 mt-4 text-2xl md:text-3xl text-[#0D1B2A]">
                Recevoir les prochaines publications.
            </h3>
            <p className="mt-3 text-[#5C6B7D] text-sm leading-relaxed">
                Un envoi mesuré, sans bruit superflu : analyses, ressources, et
                temps forts d'Aoria.
            </p>

            <label
                htmlFor="nl-email"
                className="mt-8 block text-xs uppercase tracking-[0.18em] text-[#5C6B7D]"
            >
                Adresse e-mail
            </label>
            <input
                id="nl-email"
                data-testid="newsletter-email-input"
                type="email"
                name="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="prenom.nom@exemple.fr"
                className="mt-2 w-full bg-transparent border-b border-[#0D1B2A]/40 focus:border-[#0D1B2A] py-3 outline-none text-[#1B263B] placeholder:text-[#5C6B7D]/60 transition-colors"
            />

            <button
                type="submit"
                data-testid="newsletter-submit"
                disabled={loading}
                className="btn-primary mt-8 px-6 py-3 text-sm tracking-[0.15em] uppercase disabled:opacity-60"
            >
                {loading ? "Envoi…" : "S'inscrire"}
            </button>

            <FormFeedback
                status={status}
                text={
                    status === "success"
                        ? "Merci. Votre inscription est bien enregistrée."
                        : "Une erreur est survenue. Merci de réessayer."
                }
            />
        </form>
    );
}

function BenevolatForm() {
    const [status, setStatus] = useState(null);
    const [loading, setLoading] = useState(false);
    const [form, setForm] = useState({
        name: "",
        email: "",
        domaine: "",
        message: "",
    });

    const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const res = await fetch("/", {
                method: "POST",
                headers: { "Content-Type": "application/x-www-form-urlencoded" },
                body: encode({
                    "form-name": "benevolat",
                    "bot-field": "",
                    ...form,
                }),
            });
            if (!res.ok) throw new Error("submit failed");
            setStatus("success");
            setForm({ name: "", email: "", domaine: "", message: "" });
        } catch {
            setStatus("error");
        } finally {
            setLoading(false);
        }
    };

    return (
        <form
            data-testid="benevolat-form"
            name="benevolat"
            method="POST"
            data-netlify="true"
            netlify-honeypot="bot-field"
            onSubmit={submit}
            className="border border-[#E0E1DD] bg-[#FDFDFC] p-8 md:p-10 flex flex-col h-full"
        >
            <input type="hidden" name="form-name" value="benevolat" />
            <p className="hidden">
                <label>
                    Ne pas remplir : <input name="bot-field" />
                </label>
            </p>

            <span className="eyebrow">Contribuer</span>
            <h3 className="h3 mt-4 text-2xl md:text-3xl text-[#0D1B2A]">
                Proposer votre aide à Aoria.
            </h3>
            <p className="mt-3 text-[#5C6B7D] text-sm leading-relaxed">
                Recherche, rédaction, design, ingénierie, accompagnement
                pédagogique, droit, médiation, communication : décrivez-nous le
                rôle qui aurait du sens pour vous.
            </p>

            <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                    <label className="block text-xs uppercase tracking-[0.18em] text-[#5C6B7D]">
                        Nom
                    </label>
                    <input
                        data-testid="benevolat-name"
                        type="text"
                        name="name"
                        required
                        aria-label="Nom"
                        value={form.name}
                        onChange={update("name")}
                        className="mt-2 w-full bg-transparent border-b border-[#0D1B2A]/40 focus:border-[#0D1B2A] py-3 outline-none"
                    />
                </div>
                <div>
                    <label className="block text-xs uppercase tracking-[0.18em] text-[#5C6B7D]">
                        E-mail
                    </label>
                    <input
                        data-testid="benevolat-email"
                        type="email"
                        name="email"
                        required
                        aria-label="E-mail"
                        value={form.email}
                        onChange={update("email")}
                        className="mt-2 w-full bg-transparent border-b border-[#0D1B2A]/40 focus:border-[#0D1B2A] py-3 outline-none"
                    />
                </div>
            </div>

            <div className="mt-5">
                <label className="block text-xs uppercase tracking-[0.18em] text-[#5C6B7D]">
                    Domaine d'expertise
                </label>
                <input
                    data-testid="benevolat-domaine"
                    type="text"
                    name="domaine"
                    aria-label="Domaine d'expertise"
                    value={form.domaine}
                    onChange={update("domaine")}
                    placeholder="Recherche, design, droit, éducation…"
                    className="mt-2 w-full bg-transparent border-b border-[#0D1B2A]/40 focus:border-[#0D1B2A] py-3 outline-none placeholder:text-[#5C6B7D]/60"
                />
            </div>

            <div className="mt-5">
                <label className="block text-xs uppercase tracking-[0.18em] text-[#5C6B7D]">
                    Comment souhaitez-vous contribuer ?
                </label>
                <textarea
                    data-testid="benevolat-message"
                    name="message"
                    rows={4}
                    aria-label="Comment souhaitez-vous contribuer ?"
                    value={form.message}
                    onChange={update("message")}
                    className="mt-2 w-full bg-transparent border-b border-[#0D1B2A]/40 focus:border-[#0D1B2A] py-3 outline-none resize-none"
                />
            </div>

            <button
                type="submit"
                data-testid="benevolat-submit"
                disabled={loading}
                className="btn-secondary mt-8 px-6 py-3 text-sm tracking-[0.15em] uppercase disabled:opacity-60 self-start"
            >
                {loading ? "Envoi…" : "Envoyer ma proposition"}
            </button>

            <FormFeedback
                status={status}
                text={
                    status === "success"
                        ? "Merci pour votre proposition. Nous reviendrons vers vous."
                        : "Une erreur est survenue. Merci de réessayer."
                }
            />
        </form>
    );
}

function ContactForm() {
    const [status, setStatus] = useState(null);
    const [loading, setLoading] = useState(false);
    const [form, setForm] = useState({ name: "", email: "", message: "" });

    const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const res = await fetch("/", {
                method: "POST",
                headers: { "Content-Type": "application/x-www-form-urlencoded" },
                body: encode({
                    "form-name": "contact",
                    "bot-field": "",
                    ...form,
                }),
            });
            if (!res.ok) throw new Error("submit failed");
            setStatus("success");
            setForm({ name: "", email: "", message: "" });
        } catch {
            setStatus("error");
        } finally {
            setLoading(false);
        }
    };

    return (
        <form
            data-testid="contact-form"
            name="contact"
            method="POST"
            data-netlify="true"
            netlify-honeypot="bot-field"
            onSubmit={submit}
            className="border border-[#E0E1DD] bg-[#FDFDFC] p-8 md:p-10 flex flex-col h-full"
        >
            <input type="hidden" name="form-name" value="contact" />
            <p className="hidden">
                <label>
                    Ne pas remplir : <input name="bot-field" />
                </label>
            </p>

            <span className="eyebrow">Contact</span>
            <h3 className="h3 mt-4 text-2xl md:text-3xl text-[#0D1B2A]">
                Échanger avec Aoria.
            </h3>
            <p className="mt-3 text-[#5C6B7D] text-sm leading-relaxed">
                Presse, partenariats, institutions, retours d'expérience :
                écrivez-nous. Nous lisons tout.
            </p>

            <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                    <label className="block text-xs uppercase tracking-[0.18em] text-[#5C6B7D]">
                        Nom
                    </label>
                    <input
                        data-testid="contact-name"
                        type="text"
                        name="name"
                        required
                        aria-label="Nom"
                        value={form.name}
                        onChange={update("name")}
                        className="mt-2 w-full bg-transparent border-b border-[#0D1B2A]/40 focus:border-[#0D1B2A] py-3 outline-none"
                    />
                </div>
                <div>
                    <label className="block text-xs uppercase tracking-[0.18em] text-[#5C6B7D]">
                        E-mail
                    </label>
                    <input
                        data-testid="contact-email"
                        type="email"
                        name="email"
                        required
                        aria-label="E-mail"
                        value={form.email}
                        onChange={update("email")}
                        className="mt-2 w-full bg-transparent border-b border-[#0D1B2A]/40 focus:border-[#0D1B2A] py-3 outline-none"
                    />
                </div>
            </div>

            <div className="mt-5">
                <label className="block text-xs uppercase tracking-[0.18em] text-[#5C6B7D]">
                    Votre message
                </label>
                <textarea
                    data-testid="contact-message"
                    name="message"
                    rows={5}
                    required
                    aria-label="Votre message"
                    value={form.message}
                    onChange={update("message")}
                    className="mt-2 w-full bg-transparent border-b border-[#0D1B2A]/40 focus:border-[#0D1B2A] py-3 outline-none resize-none"
                />
            </div>

            <button
                type="submit"
                data-testid="contact-submit"
                disabled={loading}
                className="btn-primary mt-8 px-6 py-3 text-sm tracking-[0.15em] uppercase disabled:opacity-60 self-start"
            >
                {loading ? "Envoi…" : "Envoyer"}
            </button>

            <FormFeedback
                status={status}
                text={
                    status === "success"
                        ? "Message bien reçu. Merci de votre prise de contact."
                        : "Une erreur est survenue. Merci de réessayer."
                }
            />
        </form>
    );
}

export default function Participate() {
    return (
        <section
            data-testid="participate-section"
            id="participer"
            className="py-24 md:py-36 bg-[#F4F5F7] border-t border-[#E0E1DD]"
        >
            <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 mb-14">
                    <div className="lg:col-span-3 reveal">
                        <span className="editorial-num text-lg">08</span>
                        <div className="hairline mt-3 pb-3" />
                        <p className="eyebrow mt-4">Participer</p>
                    </div>
                    <div className="lg:col-span-9">
                        <h2 className="reveal h2 text-4xl md:text-5xl lg:text-6xl text-[#0D1B2A] max-w-4xl">
                            Trois manières de rejoindre{" "}
                            <span className="italic text-[#415A77]">
                                le projet.
                            </span>
                        </h2>
                        <p className="reveal delay-1 mt-8 text-lg md:text-xl text-[#5C6B7D] max-w-3xl">
                            S'informer, contribuer, ou simplement nous écrire.
                            Aoria avance avec ses lecteurs et lectrices.
                        </p>
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
                    <div className="reveal">
                        <NewsletterForm />
                    </div>
                    <div className="reveal delay-1">
                        <BenevolatForm />
                    </div>
                    <div className="reveal delay-2">
                        <ContactForm />
                    </div>
                </div>

                {/* Coordonnées institutionnelles */}
                <div
                    data-testid="contact-coordinates"
                    className="reveal mt-16 md:mt-20 border border-[#E0E1DD] bg-[#FDFDFC] p-8 md:p-10 grid grid-cols-1 md:grid-cols-3 gap-8"
                >
                    <div>
                        <span className="eyebrow">Adresse postale</span>
                        <p
                            data-testid="contact-address"
                            className="mt-4 text-[#1B263B] leading-relaxed"
                        >
                            Aoria
                            <br />
                            19 rue de Wicardenne
                            <br />
                            62200 Boulogne-sur-Mer
                            <br />
                            France
                        </p>
                    </div>
                    <div>
                        <span className="eyebrow">Courriel</span>
                        <p className="mt-4 text-[#1B263B]">
                            <a
                                href="mailto:contact@aoria.org"
                                className="link-underline"
                            >
                                contact@aoria.org
                            </a>
                        </p>
                        <p className="mt-4 text-sm text-[#5C6B7D] leading-relaxed">
                            Presse, partenariats, institutions, retours
                            d'expérience.
                        </p>
                    </div>
                    <div>
                        <span className="eyebrow">Identité</span>
                        <p className="mt-4 text-sm text-[#1B263B] leading-relaxed">
                            Association déclarée régie par la loi du
                            1<sup>er</sup> juillet 1901.
                        </p>
                        <p className="mt-2 text-sm text-[#5C6B7D] font-mono tracking-wider">
                            SIREN 993 013 549
                        </p>
                    </div>
                </div>
            </div>
        </section>
    );
}
